import * as pc from "playcanvas";
import { Sim } from "../sim/sim";
import { Camera, FOV_Y, NEAR_CLIP, FAR_CLIP } from "./camera";
import type { RenderView } from "./view";
import { PcFxSystem } from "./pc/pcfx";
import { SceneAssets } from "./pc/scene";
import { ModelManager } from "./pc/models";
import { Overlay } from "./pc/overlay";
import { KIND_UNIT, KIND_BUILDING, KIND_WRECKAGE, KIND_FEATURE, OWNER_PLAYER, PROJ_ARC } from "../sim/constants";
import { F_UNDER_CONSTRUCTION } from "../sim/world";

// TORRENT renderer — 100% PlayCanvas / WebGL2 (operator directive 2026-08-02).
// A tilted perspective RTS camera (see camera.ts) drives a real pc.Camera; the
// 3D scene owns terrain, procedural low-poly units / buildings / features /
// wreckage, projectiles and explosion light-flashes; a screen-space Overlay owns
// the crisp tactical layer + strategic icons, projected through the SAME Camera.
// The sim runs OUTSIDE the engine loop exactly as before: game.ts steps the
// fixed-timestep sim and calls render(); the DOM HUD sits on top.

const RAD2DEG = 180 / Math.PI;

interface EntRec { e: pc.Entity; defId: string; kind: number; }
interface ProjRec { e: pc.Entity; key: number; }
interface Flash { e: pc.Entity; age: number; life: number; base: number; }

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export type { RenderView };

export class PcRenderer {
  readonly camera: Camera;
  readonly fx = new PcFxSystem();
  private sim: Sim;
  private app: pc.Application;
  private worldRoot: pc.Entity;
  private camEnt: pc.Entity;
  private scene: SceneAssets;
  private models: ModelManager;
  private overlay: Overlay;
  private dpr = 1;

  private ents = new Map<number, EntRec>();
  private alive = new Set<number>();

  // projectile pool: [bolt-cyan, bolt-orange, arc-cyan, arc-orange]
  private projTpl: pc.Entity[] = [];
  private projRoot: pc.Entity;
  private projPool: pc.Entity[][] = [[], [], [], []];
  private projEnts = new Map<number, ProjRec>();
  private projAlive = new Set<number>();

  // explosion dynamic lights
  private flashRoot: pc.Entity;
  private flashes: Flash[] = [];
  private flashFree: pc.Entity[] = [];

  private _eye = new pc.Vec3();
  private _tgt = new pc.Vec3();
  private _up = new pc.Vec3(0, 1, 0);

  constructor(canvas: HTMLCanvasElement, sim: Sim) {
    this.sim = sim;
    this.camera = new Camera(sim.world.gmap.worldW, sim.world.gmap.worldH);

    this.app = new pc.Application(canvas, {
      graphicsDeviceOptions: { antialias: true, alpha: false, powerPreference: "high-performance" },
    });
    this.app.setCanvasFillMode(pc.FILLMODE_NONE);
    this.app.setCanvasResolution(pc.RESOLUTION_FIXED);
    this.app.start();
    // The sim is driven by game.ts (fixed timestep, outside the engine loop); we
    // render manually from render(). Disable auto-render so it never draws behind
    // our back — we sync the hierarchy and call app.render() ourselves.
    this.app.autoRender = false;

    const scn = this.app.scene;
    scn.ambientLight = new pc.Color(0.15, 0.18, 0.23);

    this.worldRoot = new pc.Entity("world");
    this.app.root.addChild(this.worldRoot);
    this.projRoot = new pc.Entity("proj");
    this.worldRoot.addChild(this.projRoot);
    this.flashRoot = new pc.Entity("flashes");
    this.worldRoot.addChild(this.flashRoot);

    // Perspective RTS camera; position + look driven per frame from Camera.
    this.camEnt = new pc.Entity("camera");
    this.camEnt.addComponent("camera", {
      projection: pc.PROJECTION_PERSPECTIVE,
      fov: FOV_Y,
      clearColor: new pc.Color(0.02, 0.03, 0.05),
      nearClip: NEAR_CLIP,
      farClip: FAR_CLIP,
    });
    this.camEnt.camera!.toneMapping = pc.TONEMAP_NEUTRAL;
    this.app.root.addChild(this.camEnt);

    // Key light from the NW, casting shadows (only the few static buildings /
    // trees / rocks cast; mobile units use cheap blob shadows — 60fps at 200+).
    const key = new pc.Entity("key");
    key.addComponent("light", {
      type: "directional",
      color: new pc.Color(1.0, 0.96, 0.88),
      intensity: 2.1,
      castShadows: true,
      shadowResolution: 2048,
      shadowDistance: 1600,
      numCascades: 1,
      shadowType: pc.SHADOW_PCF3,
      shadowBias: 0.05,
      normalOffsetBias: 0.06,
    });
    key.setEulerAngles(52, 32, 0);
    this.app.root.addChild(key);

    // Soft cool fill from the SE, no shadows.
    const fill = new pc.Entity("fill");
    fill.addComponent("light", {
      type: "directional",
      color: new pc.Color(0.6, 0.72, 0.95),
      intensity: 0.55,
      castShadows: false,
    });
    fill.setEulerAngles(40, -150, 0);
    this.app.root.addChild(fill);

    // Scene assets + terrain (synchronous, zero-asset).
    this.scene = new SceneAssets(this.app, sim.world.gmap);
    this.scene.buildTerrain(this.worldRoot);
    this.buildProjectileTemplates();

    this.models = new ModelManager(this.app, this.scene, this.worldRoot);
    this.models.load();

    // Screen-space tactical overlay, stacked above the WebGL canvas.
    this.overlay = new Overlay(this.sim, this.camera, sim.cfg.playerFaction, sim.cfg.enemyFaction);
    canvas.parentElement?.appendChild(this.overlay.canvas);

    // Start focused on the player's commander (matches the retired 2D renderer).
    const s = sim.world.gmap.def.starts.find((st) => st.owner === OWNER_PLAYER);
    if (s) this.camera.centerOn(s.x, s.y);
  }

  private buildProjectileTemplates(): void {
    const dev = this.app.graphicsDevice;
    const mk = (mesh: pc.Mesh, c: pc.Color): pc.Entity => {
      const m = new pc.StandardMaterial();
      m.useLighting = false;
      m.emissive = c;
      m.emissiveIntensity = 4;
      m.diffuse = new pc.Color(0, 0, 0);
      m.update();
      const mi = new pc.MeshInstance(mesh, m);
      mi.castShadow = false;
      const e = new pc.Entity("proj");
      e.addComponent("render", { meshInstances: [mi], castShadows: false });
      return e;
    };
    const bolt = pc.Mesh.fromGeometry(dev, new pc.BoxGeometry({ halfExtents: new pc.Vec3(6, 0.9, 0.9) }));
    const arc = pc.Mesh.fromGeometry(dev, new pc.SphereGeometry({ radius: 2.6 }));
    const cyan = new pc.Color(0.55, 0.91, 1.0);
    const orange = new pc.Color(1.0, 0.69, 0.4);
    this.projTpl = [mk(bolt, cyan), mk(bolt, orange), mk(arc, cyan), mk(arc, orange)];
  }

  resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    const cw = Math.max(1, Math.floor(w * this.dpr));
    const ch = Math.max(1, Math.floor(h * this.dpr));
    const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
    canvas.width = cw;
    canvas.height = ch;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    this.app.graphicsDevice.setResolution(cw, ch);
    this.camera.resize(w, h);
    this.overlay.resize(this.dpr, w, h);
  }

  private syncCamera(): void {
    const cam = this.camera;
    cam.getEye(this._eye);
    cam.getTarget(this._tgt);
    // Screen shake → equal world offset on eye + target (keeps the look dir).
    const sx = this.fx.shakeX / cam.zoom;
    const sz = this.fx.shakeY / cam.zoom;
    this.camEnt.setPosition(this._eye.x + sx, this._eye.y, this._eye.z + sz);
    this.camEnt.lookAt(this._tgt.x + sx, this._tgt.y, this._tgt.z + sz, this._up.x, this._up.y, this._up.z);
    this.camEnt.camera!.fov = FOV_Y;
  }

  destroy(): void {
    this.overlay.canvas.remove();
    this.app.destroy();
  }

  render(alpha: number, view: RenderView): void {
    this.syncCamera();
    this.syncEntities(alpha);
    this.syncProjectiles(alpha);
    this.updateFlashes();

    // Force world transforms current, then render manually (sim is external).
    this.app.root.syncHierarchy();
    this.app.render();

    this.overlay.draw(alpha, view, this.fx);
  }

  // --- 3D entity reconciliation ---------------------------------------------
  private syncEntities(alpha: number): void {
    const w = this.sim.world;
    const iconZoom = this.camera.isIconZoom();
    this.alive.clear();

    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      const kind = w.kind[e];
      if (kind !== KIND_UNIT && kind !== KIND_BUILDING && kind !== KIND_WRECKAGE && kind !== KIND_FEATURE) continue;
      const def = w.def(e);
      this.alive.add(e);

      let rec = this.ents.get(e);
      if (rec && (rec.defId !== def.id || rec.kind !== kind)) {
        this.models.release(rec.e, rec.defId, rec.kind);
        this.ents.delete(e);
        rec = undefined;
      }
      if (!rec) {
        const ent = this.models.acquire(def, kind);
        if (!ent) continue;
        rec = { e: ent, defId: def.id, kind };
        this.ents.set(e, rec);
      }

      // Icon-zoom: hide 3D units/buildings (overlay draws icons); keep features/
      // wreckage in 3D. Under-construction stays visible so the ghost reads.
      const hide = iconZoom && (kind === KIND_UNIT || kind === KIND_BUILDING) && (w.flags[e] & F_UNDER_CONSTRUCTION) === 0;
      rec.e.enabled = !hide;
      if (hide) continue;

      const ix = kind === KIND_UNIT ? lerp(w.prevX[e], w.posX[e], alpha) : w.posX[e];
      const iy = kind === KIND_UNIT ? lerp(w.prevY[e], w.posY[e], alpha) : w.posY[e];
      const gy = this.scene.elevationY(ix, iy);
      rec.e.setPosition(ix, gy, iy);
      const heading = kind === KIND_UNIT ? lerpAngle(w.prevHeading[e], w.heading[e], alpha) : w.heading[e];
      // Sprites face +x (sim heading 0); meshes are authored facing +Z (north),
      // so rotate by heading about Y with the +x→+z quarter-turn folded in.
      rec.e.setLocalEulerAngles(0, -heading * RAD2DEG - 90, 0);
    }

    for (const [id, rec] of this.ents) {
      if (!this.alive.has(id)) {
        this.models.release(rec.e, rec.defId, rec.kind);
        this.ents.delete(id);
      }
    }
  }

  // --- 3D projectiles --------------------------------------------------------
  private acquireProj(key: number): pc.Entity {
    const free = this.projPool[key];
    if (free.length) { const e = free.pop()!; e.enabled = true; return e; }
    const e = this.projTpl[key].clone() as pc.Entity;
    e.enabled = true;
    this.projRoot.addChild(e);
    return e;
  }
  private releaseProj(e: pc.Entity, key: number): void {
    e.enabled = false;
    this.projPool[key].push(e);
  }

  private syncProjectiles(alpha: number): void {
    const p = this.sim.proj;
    this.projAlive.clear();
    for (let i = 0; i < p.count; i++) {
      if (!p.alive[i]) continue;
      this.projAlive.add(i);
      const isArc = p.ptype[i] === PROJ_ARC;
      const fac = p.faction[i];
      const key = (isArc ? 2 : 0) + (fac === 1 ? 1 : 0);

      let rec = this.projEnts.get(i);
      if (rec && rec.key !== key) { this.releaseProj(rec.e, rec.key); this.projEnts.delete(i); rec = undefined; }
      if (!rec) { rec = { e: this.acquireProj(key), key }; this.projEnts.set(i, rec); }

      const x = lerp(p.px[i], p.x[i], alpha);
      const y = lerp(p.py[i], p.y[i], alpha);
      const z = lerp(p.pz[i], p.z[i], alpha); // sim arc height above ground
      const gy = this.scene.elevationY(x, y);
      // Real 3D arc: the shell rides its sim height above the terrain.
      rec.e.setPosition(x, gy + 5 + (isArc ? z : 0), y);
      if (isArc) rec.e.setLocalEulerAngles(0, 0, 0);
      else rec.e.setLocalEulerAngles(0, -Math.atan2(p.vy[i], p.vx[i]) * RAD2DEG, 0);
    }
    for (const [i, rec] of this.projEnts) {
      if (!this.projAlive.has(i)) { this.releaseProj(rec.e, rec.key); this.projEnts.delete(i); }
    }
  }

  // --- Explosion dynamic light flashes --------------------------------------
  private updateFlashes(): void {
    for (const f of this.fx.flashes) {
      let e = this.flashFree.pop();
      if (!e) {
        e = new pc.Entity("flash");
        e.addComponent("light", { type: "omni", color: new pc.Color(1.0, 0.7, 0.35), range: 200, castShadows: false });
        this.flashRoot.addChild(e);
      }
      const gy = this.scene.elevationY(f.x, f.y);
      e.setPosition(f.x, gy + f.radius * 0.4 + 6, f.y);
      const lc = e.light!;
      lc.range = Math.max(60, f.radius * (f.big ? 5 : 3.2));
      e.enabled = true;
      this.flashes.push({ e, age: 0, life: f.big ? 0.28 : 0.14, base: f.big ? 10 : 4.5 });
    }
    this.fx.flashes.length = 0;

    const dt = 1 / 60;
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const fl = this.flashes[i];
      fl.age += dt;
      const k = fl.age / fl.life;
      if (k >= 1) {
        fl.e.enabled = false;
        this.flashFree.push(fl.e);
        this.flashes.splice(i, 1);
        continue;
      }
      fl.e.light!.intensity = fl.base * (1 - k) * (1 - k);
    }
  }
}
